import React, { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { TbSearch } from "react-icons/tb";
import { Visibility, Delete } from "@mui/icons-material";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { PageProps } from "../../models/ui.model";
import { useCommentsTable } from "./hooks/useCommentsTable";
import CommentDetailDialog from "./components/CommentDetailDialog";

const formatDate = (iso?: string) => {
  if (!iso) return "N/A";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "Fecha inválida";
  }
};

const Comments: React.FC<PageProps> = ({ setActiveItem }) => {
  const {
    // datos ya filtrados + paginados
    comments,
    total,

    // estado ui
    loading,
    errorMsg,
    snackbar,

    // filtros/paginación
    searchTerm,
    rowsPerPage,
    page,
    setPage,
    handleChangeSearch,
    handleChangeRowsPerPage,

    // ver
    viewDialogOpen,
    viewingComment,
    openView,
    closeView,

    // delete
    deleteDialogOpen,
    deletingComment,
    confirmDelete,
    closeDelete,
    doDelete,

    // snackbar
    closeSnackbar,
  } = useCommentsTable();

  // Obtener el rol del usuario
  const userProfile = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userProfile') || 'null');
    } catch {
      return null;
    }
  }, []);

  const canDelete = useMemo(() => {
    const role = userProfile?.role;
    return role === "admin" || role === "manager";
  }, [userProfile]);

  useEffect(() => {
    setActiveItem?.("Comentarios");
  }, [setActiveItem]);

  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  return (
    <Container maxWidth={false} disableGutters sx={{ p: 3, backgroundColor: "#fff", minHeight: "100vh" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: 40 }}>
          Comentarios
        </Typography>
      </Box>

      {/* Búsqueda */}
      <Box display="flex" mb={4} gap={2}>
        <TextField
          fullWidth
          placeholder="Busca por ID, comentario, origen o usuario"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => handleChangeSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TbSearch size="22px" strokeWidth="1.5px" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Tabla / Loader / Error */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
          <LoadingSpinner />
        </Box>
      ) : errorMsg ? (
        <Alert severity="error">{errorMsg}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Comentario</TableCell>
                <TableCell>Origen</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comments.length ? (
                comments.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell width={120}>{row.id}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Tooltip title={row.comment} placement="top" arrow>
                        <span
                          style={{
                            display: "inline-block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                        >
                          {row.comment}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell width={200}>
                      {row?.source === "precedente"
                        ? "Análisis de Precedentes"
                        : row?.source === "Soporte"
                        ? "Campo de soporte"
                        : "Comentario"}
                    </TableCell>
                    <TableCell width={220}>{row.userName}</TableCell>
                    <TableCell width={140}>{formatDate(row.createdAt)}</TableCell>
                    <TableCell align="right" width={140}>
                      <Tooltip title="Ver detalle">
                        <IconButton onClick={() => openView(row)} aria-label="Ver comentario">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {canDelete && (
                        <Tooltip title="Eliminar">
                          <IconButton
                            onClick={() => confirmDelete(row)}
                            aria-label="Eliminar comentario"
                            sx={{ color: "#D32F2F" }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No se encontraron comentarios
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Footer de paginación */}
      {!loading && total > 0 && (
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <TextField
            select
            label="Filas por página"
            value={rowsPerPage}
            onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))}
            variant="outlined"
            size="small"
            sx={{ width: 160 }}
          >
            {[5, 10, 20, 30].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            variant="outlined"
            shape="rounded"
          />

          <Typography color="textSecondary">
            {total === 0 ? "0-0" : `${(page - 1) * rowsPerPage + 1}-${Math.min(page * rowsPerPage, total)}`} de {total}
          </Typography>
        </Box>
      )}

      {/* Modal: Ver comentario */}
      <CommentDetailDialog
        open={viewDialogOpen}
        onClose={closeView}
        data={
          viewingComment
            ? {
                id: viewingComment.id,
                comment: viewingComment.comment,
                source: viewingComment.source,
                userName: viewingComment.userName,
                createdAt: viewingComment.createdAt,
                analysisId: viewingComment.analysisId,
                analysisFile: viewingComment.analysisFile,
              }
            : null
        }
      />

      {/* Modal: Confirmar eliminación */}
      <Dialog open={deleteDialogOpen} onClose={closeDelete}>
        <DialogTitle>Eliminar comentario</DialogTitle>
        <DialogContent dividers>
          <Typography>
            ¿Seguro que deseas eliminar el comentario <strong>#{deletingComment?.id}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete}>Cancelar</Button>
          <Button onClick={doDelete} sx={{ color: "#D32F2F" }}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.text}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Comments;
